import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import { useParams, Redirect } from 'react-router-dom';
import ReactPlayer from 'react-player';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const CoursePlay = () => {
    const { slug } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
    const [completed, setCompleted] = useState([]);
    const [redirect, setRedirect] = useState(false);

    const token = localStorage.getItem("token");

    // ✅ Check course access from localStorage
    const purchased = JSON.parse(localStorage.getItem("purchased_courses") || "[]");
    const hasAccess = purchased.includes(slug);

    useEffect(() => {
        if (!hasAccess) {
            setRedirect(true);
            return;
        }

        const fetchCourseData = async () => {
            setLoading(true);
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            try {
                const [courseRes, progressRes] = await Promise.all([
                    axios.get(`${API_BASE_URL}/courses/${slug}`, { headers }),
                    token
                        ? axios.get(`${API_BASE_URL}/courses/${slug}/progress`, { headers })
                        : Promise.resolve({ data: { completed_videos: [] } }),
                ]);

                if (!courseRes.data?.data) {
                    setRedirect(true);
                } else {
                    setCourse(courseRes.data.data);
                    setCompleted(progressRes.data.completed_videos || []);
                }
            } catch (error) {
                console.error("Fetch error:", error);
                setRedirect(true);
            } finally {
                setLoading(false);
            }
        };

        fetchCourseData();
    }, [slug, token, hasAccess]);

    const handleVideoEnd = () => {
        if (!completed.includes(currentVideoIndex)) {
            const updatedCompleted = [...completed, currentVideoIndex];
            setCompleted(updatedCompleted);

            if (token) {
                axios.post(`${API_BASE_URL}/courses/${slug}/progress`, {
                    video_index: currentVideoIndex
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                }).catch((err) => console.error("Progress save error", err));
            }
        }
    };

    if (!hasAccess || redirect) return <Redirect to="/courses" />;
    if (loading) return <p className="text-center mt-5">Loading course details...</p>;
    if (!course) return <p className="text-center mt-5 text-danger">Course not found.</p>;

    const playlist = course.playlist || [];
    const progress = playlist.length
        ? Math.round((completed.length / playlist.length) * 100)
        : 0;

    return (
        <>
            <SEO title={`${course.title} || PWU`} />
            <Layout>
                <div className="container mt-5 d-flex gap-4 flex-wrap">
                    {/* Sidebar */}
                    <aside
                        style={{
                            flex: "0 0 300px",
                            maxHeight: "80vh",
                            overflowY: "auto",
                            borderRight: "1px solid #ddd",
                            paddingRight: "15px"
                        }}
                    >
                        <h4 className="mb-4">{course.title} - Playlist</h4>
                        <ul className="list-group">
                            {playlist.map((video, index) => (
                                <li
                                    key={index}
                                    className={`list-group-item d-flex justify-content-between align-items-center ${
                                        index === currentVideoIndex ? "active" : ""
                                    }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => setCurrentVideoIndex(index)}
                                >
                                    {video.title}
                                    {completed.includes(index)
                                        ? <span>✅</span>
                                        : index === currentVideoIndex
                                            ? <span>▶️</span>
                                            : null
                                    }
                                </li>
                            ))}
                        </ul>
                    </aside>

                    {/* Main Video Player */}
                    <main style={{ flex: "1", paddingBottom: "100px" }}>
                        {/* Progress */}
                        <div className="mb-3">
                            <div className="progress" style={{ height: "10px" }}>
                                <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${progress}%` }}
                                    aria-valuenow={progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>
                            <small className="text-muted">{progress}% Complete</small>
                        </div>

                        {/* Player */}
                        {playlist.length > 0 && playlist[currentVideoIndex] ? (
                            <ReactPlayer
                                url={playlist[currentVideoIndex].url}
                                controls
                                width="100%"
                                height="70vh"
                                onEnded={handleVideoEnd}
                            />
                        ) : (
                            <p>No videos available in this course.</p>
                        )}
                    </main>
                </div>
            </Layout>
        </>
    );
};

export default CoursePlay;
